import { default as Configuracao, default as config } from '../misc/Configuracao';
import { CategoriaMinuta } from '../modelo';
import MensagemStore from '../store/MensagemStore';
import { apresentarApiErroPadrao } from '@/utils/ApiErroPadraoUtils';
import fetch from './AxiosAdapter';
import {
    UsuarioStore
} from "@/store";

class CategoriaApi {

    private procurarListaCategoriasRecursiva(payload: any, profundidade: number = 0): CategoriaMinuta[] {
        if (!payload || profundidade > 4) {
            return [];
        }

        if (Array.isArray(payload)) {
            if (payload.some((item: any) => item?.descricao !== undefined || item?.numero !== undefined)) {
                return payload as CategoriaMinuta[];
            }

            for (const item of payload) {
                const encontrado = this.procurarListaCategoriasRecursiva(item, profundidade + 1);
                if (encontrado.length > 0) {
                    return encontrado;
                }
            }
            return [];
        }

        if (typeof payload === 'object') {
            for (const valor of Object.values(payload)) {
                const encontrado = this.procurarListaCategoriasRecursiva(valor, profundidade + 1);
                if (encontrado.length > 0) {
                    return encontrado;
                }
            }
        }

        return [];
    }

    private extrairCategorias(payload: any): CategoriaMinuta[] {
        if (!payload) {
            return [];
        }

        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return this.extrairCategorias(parsed);
            } catch {
                return [];
            }
        }

        if (Array.isArray(payload)) {
            return payload;
        }

        const candidatosDiretos = [
            payload?.content,
            payload?.data,
            payload?.data?.content,
            payload?.result,
            payload?.resultado,
            payload?.items,
            payload?._embedded?.categorias,
            payload?._embedded?.categoriaMinutaList,
        ];

        for (const candidato of candidatosDiretos) {
            if (Array.isArray(candidato)) {
                return candidato as CategoriaMinuta[];
            }

            if (typeof candidato === 'string') {
                try {
                    const parsed = JSON.parse(candidato);
                    const extraidas = this.extrairCategorias(parsed);
                    if (extraidas.length > 0) {
                        return extraidas;
                    }
                } catch {
                    // ignora candidato string invalido
                }
            }
        }

        for (const valor of Object.values(payload)) {
            if (Array.isArray(valor) && valor.some((item: any) => item?.descricao !== undefined)) {
                return valor as CategoriaMinuta[];
            }
        }

        const recursivo = this.procurarListaCategoriasRecursiva(payload);
        if (recursivo.length > 0) {
            return recursivo;
        }

        return [];
    }

    async verificarResultado(result) {
        if (result.status >= 400) {
            if (apresentarApiErroPadrao(result?.data)) {
                throw result.data;
            }
            const erro = result.data;
            throw new Error(erro ? erro.mensagem : null);
        }
    }

    async salvar(categoria: CategoriaMinuta) {
        categoria.movimentoCNJ = undefined;
        const body = CategoriaMinuta.novo(categoria);
        let result;
        const numero = categoria.numero ? categoria.numero : '';
        try {
            result = await fetch(config.urlBaseGE + 'api/categorias/' + numero,
                {
                    method: categoria.numero ? 'PUT' : 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: body
                });
            await this.verificarResultado(result);
        } catch (erro) {
            if (apresentarApiErroPadrao(erro)) {
                throw erro;
            }
            const message = "Não foi possível salvar a categoria. " + erro + " " + (result ? result.statusText : "");
            console.error(message);
            throw new Error(message);
        }

        return result;
    }

    async salvarCategoriasNasMinutas(numerosMinutas: number[], numerosCategorias: number[]) {

        let result;
        const body = {
            'numerosMinutas': numerosMinutas,
            'numerosCategorias': numerosCategorias
        };

        try {
            result = await fetch(Configuracao.urlSalvarCategoriasNasMinutas,
                {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: body
                });
            await this.verificarResultado(result);
            MensagemStore.apresentarMensagem('Categorias adicionadas às minutas com sucesso.');
        } catch (erro) {
            if (apresentarApiErroPadrao(erro)) {
                throw erro;
            }
            const message = "Não foi possível salvar a(s) categoria(s) na(s) minuta(s). " + erro + " " + (result ? result.statusText : "");
            console.error(message);
            throw new Error(message);
            MensagemStore.apresentarErro(message);
        }

        return result;
    }

    async buscarPorOrgaoJulgador(orgaoJulgador): Promise<CategoriaMinuta[]> {
        const siglasTentativa = Array.from(new Set([
            orgaoJulgador,
            (orgaoJulgador || '').trim(),
            (orgaoJulgador || '').trim().toUpperCase(),
            (UsuarioStore.gabinete || '').trim(),
            (UsuarioStore.gabinete || '').trim().toUpperCase(),
        ].filter(sigla => !!sigla)));

        let ultimoErro;
        let houveResposta200 = false;

        for (const sigla of siglasTentativa) {
            const urlsTentativa = [
                config.urlBaseGE + 'api/categorias?siglaOrgaoJulgador=' + sigla,
                config.urlBaseGE + 'api/categorias/' + sigla,
                config.urlBaseGE + 'api/categorias',
                config.urlBaseGE + 'api/categoria?siglaOrgaoJulgador=' + sigla,
                config.urlBaseGE + 'api/categoria',
            ];

            for (const url of urlsTentativa) {
                let result;
                try {
                    result = await fetch(url, {
                        timeout: 30000
                    });
                } catch (error) {
                    if (window.location.hostname === 'localhost') {
                        console.info('[CategoriaApi] falha request', { url, sigla, erro: String(error) });
                    }
                    ultimoErro = error;
                    continue;
                }

                if (result.status !== 200) {
                    if (window.location.hostname === 'localhost') {
                        console.info('[CategoriaApi] status nao-200', { url, sigla, status: result.status });
                    }
                    ultimoErro = new Error('Não foi possível recuperar as categorias de minutas. Tente novamente.');
                    continue;
                }

                houveResposta200 = true;
                const payload: any = result.data;
                const categorias = this.extrairCategorias(payload);

                if (window.location.hostname === 'localhost') {
                    console.info('[CategoriaApi] retorno', {
                        url,
                        sigla,
                        tipoPayload: typeof payload,
                        qtdCategorias: categorias.length,
                    });
                }

                categorias.sort((categoria1, categoria2) => {
                    if (categoria1.descricao < categoria2.descricao)
                        return -1;
                    if (categoria1.descricao > categoria2.descricao)
                        return 1;
                    return 0;
                });

                if (categorias.length > 0) {
                    return categorias;
                }
            }

            // segue para proxima sigla ate encontrar categorias
        }

        if (houveResposta200) {
            return [];
        }

        console.error(ultimoErro);
        throw new Error('Ocorreu um erro em sua solicitação. Por favor, tente novamente. Caso persista comunique ao administrador do sistema.');
    }

    async buscarPorMinuta(idProcesso: number, idTipoMinuta: number):Promise<CategoriaMinuta[]> {
        let result;
        const url = config.urlBaseGE + 'api/minutas/' + idProcesso + '/' + idTipoMinuta + '/categorias?siglaOrgaoJulgador=' + UsuarioStore.localizacao.siglaOrgaoJulgador;
        try {
            const result = await fetch<CategoriaMinuta[]>(url);
            const categorias = result.data;
            if (result.status != 200) {
                throw new Error('Não foi possível recuperar as categorias da minuta. Tente novamente.');
            }
            return categorias;
        } catch (error) {
            console.error(result);
            if (apresentarApiErroPadrao(error)) {
                throw error;
            }
            throw new Error('Ocorreu um erro ao tentar carregar as categorias de minuta. Por favor, tente novamente. Caso persista comunique ao administrador do sistema.');
        }
    }

    async excluir(numeroCategoria) {
        let result;

        const url = config.urlBaseGE + 'api/categorias/' + numeroCategoria;

        try {
            result = await fetch(url, { method: 'DELETE' });
            await this.verificarResultado(result);
        } catch (erro) {
            if (apresentarApiErroPadrao(erro)) {
                throw erro;
            }
            let message = "Não foi possível excluir a categoria. " + erro + " " + (result ? result.statusText : "");
            if (erro.toString().indexOf("Error: Transaction rolled back") > -1) {
                message = "Não foi possível excluir a categoria. Verifique se existem modelos ou minutas associadas a esta categoria. Se existirem, desassocie e tente novamente.";
            }
            console.error(message);
            throw new Error(message);
        }

        return result;
    }
}

export default new CategoriaApi();
